/**
 * One step of a family's checklist, by its item: the step page for its type.
 *
 * The server decides whether this family may open the item, and refuses
 * another family's like one that doesn't exist; a refusal reads as a plain
 * message, while a request that failed offers to try again. A step from
 * another of the family's years goes to the URL year's own page, so switching
 * years never shows the wrong one. A finished step (done, waived or not
 * needed), or the office viewing as the family, opens read-only (a done Who's
 * coming the family can still change offers to change it). A locked agreement
 * waiting on a program links to Who's coming.
 */
import { Link, Navigate, useOutletContext, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import type { ApolloError } from '@apollo/client';
import type { AppLayoutContext } from '~/types';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';
import { useEnrollYear } from './enrollYear';
import { NO_ENROLLMENT, Notice } from './shared';
import { useChecklistStep } from './useChecklistStep';
import { changeWhoPath, Section, StepHeader, type StepProps } from './steps/stepParts';
import AgreementStep from './steps/AgreementStep';
import AutoPayStep from './steps/AutoPayStep';
import ChildDetailsStep from './steps/ChildDetailsStep';
import ConsentStep from './steps/ConsentStep';
import FeeStep from './steps/FeeStep';
import HouseholdStep from './steps/HouseholdStep';
import TodoStep from './steps/TodoStep';
import WhoStep from './steps/WhoStep';

// Finished, as the overview counts it: nothing for the family to answer.
const FINISHED_STATES = ['DONE', 'WAIVED', 'NOT_NEEDED'];

/** The page for each type of step; the server sends a step only the context of its own type. */
const STEP_PAGES: Record<string, ComponentType<StepProps>> = {
  who: WhoStep,
  household: HouseholdStep,
  child_details: ChildDetailsStep,
  consent: ConsentStep,
  todo: TodoStep,
  agreement: AgreementStep,
  auto_pay: AutoPayStep,
  fee: FeeStep,
};

/** The server refused the step (not this family's, or no such item), rather than failing to answer. */
function refused(error: ApolloError | undefined): boolean {
  const networkError = error?.networkError;
  return !!networkError && 'statusCode' in networkError && [401, 403].includes(networkError.statusCode);
}

export default function EnrollStep() {
  const { school } = useOutletContext<AppLayoutContext>();
  const { yearSlug, itemId } = useParams();
  const enrollYear = useEnrollYear();
  const ready = enrollYear.status === 'ready';
  const { readOnly: viewingAs, basePath } = useViewAsFamily(school?.slug);
  const { data, loading, error, refetch } = useChecklistStep(ready ? school?.id : undefined, itemId);
  const overviewPath = `${basePath}/years/${yearSlug}/enroll`;

  if (enrollYear.status === 'unavailable') {
    return (
      <div className="p-4 md:p-6">
        <Notice title="Enrollment">{NO_ENROLLMENT}</Notice>
      </div>
    );
  }
  if (enrollYear.status === 'loading' || (loading && !data)) {
    return <div className="p-4 text-muted-foreground md:p-6">Loading…</div>;
  }

  const step = data?.checklistStep;
  if (error && !refused(error)) {
    const tryAgain = async () => {
      try {
        await refetch();
      } catch {
        // The failure shows again until a try works.
      }
    };
    return (
      <Page overviewPath={overviewPath}>
        <Card>
          <CardContent className="space-y-4 p-4 md:p-6">
            <p className="text-muted-foreground">We couldn't load this step.</p>
            <Button type="button" variant="outline" className="w-full md:w-auto" onClick={tryAgain}>Try again</Button>
          </CardContent>
        </Card>
      </Page>
    );
  }
  if (error || !step) {
    return (
      <Page overviewPath={overviewPath}>
        <Notice title="Enrollment">This step isn't available.</Notice>
      </Page>
    );
  }
  if (step.yearSlug !== yearSlug) return <Navigate to={overviewPath} replace />;

  const StepPage = STEP_PAGES[step.item.stepType];
  const stepPath = (nextItemId: string) => `${overviewPath}/${nextItemId}`;
  // An agreement waiting on a program the family chooses on Who's coming links there.
  const lockedLink = !viewingAs && step.agreement?.programsMissing && step.agreement.whoItemId
    ? changeWhoPath(stepPath, step.agreement.whoItemId)
    : null;

  return (
    <Page overviewPath={overviewPath}>
      {step.item.state === 'LOCKED' && step.item.lockedReason ? (
        <div className="space-y-6">
          <StepHeader step={step} />
          <Section>
            {lockedLink ? (
              <Link to={lockedLink} className="inline-flex min-h-11 items-center text-primary underline underline-offset-2">
                {step.item.lockedReason}
              </Link>
            ) : (
              <p className="text-muted-foreground">{step.item.lockedReason}</p>
            )}
          </Section>
        </div>
      ) : StepPage ? (
        <StepPage
          key={step.item.id}
          step={step}
          readOnly={viewingAs || FINISHED_STATES.includes(step.item.state)}
          viewingAs={viewingAs}
          overviewPath={overviewPath}
          stepPath={stepPath}
        />
      ) : (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold md:text-3xl">{step.item.title}</h1>
          <Card>
            <CardContent className="p-4 md:p-6">
              <p className="text-muted-foreground">This step isn't ready yet</p>
            </CardContent>
          </Card>
        </div>
      )}
    </Page>
  );
}

function Page({ overviewPath, children }: { overviewPath: string; children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4 md:p-6">
      <Link
        to={overviewPath}
        className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        Back to enrollment
      </Link>
      {children}
    </div>
  );
}
